import axios from 'axios';
import router from '@/router';
import * as types from '../mutation-types';
import {createUrlQueryString} from '@/utils/urls';
import {performApiRequest} from '@/store/helpers/vuex-helpers';
import type {CompanyInterface} from '@/types/company';
import type {Module} from 'vuex';
import type {RootState} from '@/types/RootState';
import {push} from 'notivue'

interface ROLES {
    name: string;
    guard_name: string;
    created_at: string;
    updated_at: string;
}

interface PERMISSIONS {
    name: string;
    guard_name: string;
    created_at: string;
    updated_at: string;
}

export interface SuperUserState {
    loading: Record<string, boolean>,
    users: Array<any>,
    companies: CompanyInterface[],
    companiesMeta: {
        current_page: number,
        from: number,
        last_page: number,
        per_page: number,
        to: number,
        total: number,
    },
    currentWorkingCompany: any,
    currentWorkingUser: any,
    roles: Array<ROLES>,
    permissions: Array<PERMISSIONS>,
    media: Array<any>,
    mediaDirectories: Array<any>,
    mediaPath: string,
    allListingImages: Array<any>,
    serverDetails: any,
    basicStats: {
        active_users: Array<any>,
        subscribers: any,
        users: number | null,
        trials: number | null,
        listings: number | null,
        automations: number | null,
        bookings: number | null,
        connections: {
            tesla_accounts: number | null,
            turo_accounts: number | null,
            getaround_accounts: number | null,
        },
    },
    featureWaitlist: Array<any>,
    featuresStatus: Array<any>,
    cache: Array<any>,
    allFeatures: Array<any>,
}

const state: SuperUserState = {
    loading: {
        users: false,
        companies: false,
        roles: false,
        permissions: false,
        automations: false,
        bookings: false,
        connections: false,
        listings: false,
        subscribers: false,
        activeUsers: false,
        turoTrip: false,
        upload: false,
    },
    users: [],
    companies: [],
    companiesMeta: {
        current_page: 1,
        from: 1,
        last_page: 1,
        per_page: 1,
        to: 1,
        total: 1,
    },
    currentWorkingCompany: {},
    currentWorkingUser: {},
    roles: [],
    permissions: [],
    media: [],
    mediaDirectories: [],
    mediaPath: '',
    allListingImages: [],
    serverDetails: {},
    basicStats: {
        active_users: [],
        subscribers: {},
        users: null,
        trials: null,
        listings: null,
        automations: null,
        bookings: null,
        connections: {
            tesla_accounts: null,
            turo_accounts: null,
            getaround_accounts: null,
        },
    },

    featureWaitlist: [],
    featuresStatus: [],
    cache: [],
    allFeatures: [],
};

const getters = {
    loading: (state: SuperUserState) => state.loading,
    allUsers: (state: SuperUserState) => state.users,
    allCompanies: (state: SuperUserState) => state.companies,
    companiesMeta: (state: SuperUserState) => state.companiesMeta,
    currentWorkingUser: (state: SuperUserState) => state.currentWorkingUser,
    currentWorkingCompany: (state: SuperUserState) => state.currentWorkingCompany,
    roles: (state: SuperUserState) => state.roles,
    permissions: (state: SuperUserState) => state.permissions,
    mediaDirectories: (state: SuperUserState) => state.mediaDirectories,
    media: (state: SuperUserState) => state.media,
    mediaPath: (state: SuperUserState) => state.mediaPath,
    allListingImages: (state: SuperUserState) => state.allListingImages,
    basicStats: (state: SuperUserState) => state.basicStats,
    serverDetails: (state: SuperUserState) => state.serverDetails,
    featureWaitlist: (state: SuperUserState) => state.featureWaitlist,
    featuresStatus: (state: SuperUserState) => state.featuresStatus,
    allFeatures: (state: SuperUserState) => state.allFeatures,
    cache: (state: SuperUserState) => state.cache,
};

const mutations = {
    [types.SET_SU_LOADING](state: SuperUserState, loading: { subject: string, loading: boolean }) {
        state.loading[loading.subject] = loading.loading;
    },
    [types.SET_ALL_USERS](state: SuperUserState, users) {
        state.users = users;
    },
    [types.SET_ALL_COMPANIES](state: SuperUserState, companies) {
        state.companies = companies.data ?? [];

        state.companiesMeta = {
            current_page: companies.current_page,
            from: companies.from,
            last_page: companies.last_page,
            per_page: companies.per_page,
            to: companies.to,
            total: companies.total,
        };
    },
    [types.SET_CURRENT_WORKING_COMPANY](state: SuperUserState, company) {
        state.currentWorkingCompany = company;
    },
    [types.SET_CURRENT_WORKING_USER](state: SuperUserState, user) {
        state.currentWorkingUser = user;
    },
    [types.SET_ALL_ROLES](state: SuperUserState, roles: Array<ROLES>) {
        state.roles = roles;
    },
    [types.SET_ALL_PERMISSIONS](state: SuperUserState, permissions: Array<PERMISSIONS>) {
        state.permissions = permissions;
    },
    [types.SET_ALL_MEDIA_DIRECTORIES](state: SuperUserState, mediaDirectories) {
        state.mediaDirectories = mediaDirectories;
    },
    [types.SET_ALL_MEDIA_IN_DIRECTORY](state: SuperUserState, media) {
        state.media = media;
    },
    [types.SET_MEDIA_BASE_PATH](state: SuperUserState, path) {
        state.mediaPath = path;
    },
    [types.SET_ALL_LISTING_IMAGES](state: SuperUserState, images) {
        state.allListingImages = images;
    },
    [types.SET_ALL_SERVER_DETAILS](state: SuperUserState, details) {
        state.serverDetails = details;
    },
    [types.SET_SA_FEATURE_WAITLIST](state: SuperUserState, waitlist) {
        state.featureWaitlist = waitlist;
    },
    [types.SET_SA_FEATURES_STATUS](state: SuperUserState, status) {
        state.featuresStatus = status;
    },
    [types.SET_SU_ALL_FEATURES](state: SuperUserState, allFeatures) {
        state.allFeatures = allFeatures;
    },
    [types.SET_SA_CACHE](state: SuperUserState, cache) {
        state.cache = cache;
    },
    [types.SET_CURRENT_WORKING_SU_BASIC_STATS](state: SuperUserState, stats) {
        state.basicStats[Object.keys(stats)[0]] = stats[Object.keys(stats)[0]];
    },
};

const actions = {
    async fetchAllUsers({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get('/v1/su/users'));
        commit('SET_ALL_USERS', responseData.users);
    },

    async fetchOneUsers({commit}, uuid) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get(`/v1/su/users/${uuid}`));
        const user = responseData.user;
        user.features = responseData.features;
        commit('SET_CURRENT_WORKING_USER', user);
    },

    async fetchUserOverview({commit}, uuid) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get(`/v1/su/users/${uuid}/overview`));
        const user = responseData.user;
        user.features = responseData.features;
        commit('SET_CURRENT_WORKING_USER', user);
    },

    async previewUserPurge({commit}, uuid: string) {
        return await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get(`/v1/su/users/${uuid}/purge-preview`));
    },

    async executeUserPurge({commit}, payload: {
        uuid: string;
        nonce: string;
        confirm_text: string;
        mode: 'soft' | 'hard'
    }) {
        return await performApiRequest(commit, 'SET_SU_LOADING', 'users', () =>
            axios.post(`/v1/su/users/${payload.uuid}/purge`, {
                nonce: payload.nonce,
                confirm_text: payload.confirm_text,
                mode: payload.mode,
            }),
        );
    },

    async previewCompanyPurge({commit}, uuid: string) {
        return await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get(`/v1/su/company/${uuid}/purge-preview`));
    },

    async executeCompanyPurge({commit}, payload: {
        uuid: string;
        nonce: string;
        confirm_text: string;
        mode: 'soft' | 'hard'
    }) {
        return await performApiRequest(commit, 'SET_SU_LOADING', 'users', () =>
            axios.post(`/v1/su/company/${payload.uuid}/purge`, {
                nonce: payload.nonce,
                confirm_text: payload.confirm_text,
                mode: payload.mode,
            }),
        );
    },

    async purgeFeatures({commit}) {
        await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.post('/v1/su/features/purge'));
    },
    async liftFeatureForEveryone({commit}, payload: {
        feature: string,
        notify: boolean,
        stream: 'transactional' | 'broadcast',
        release: boolean,
    }) {
        return await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.post('/v1/su/features/lift-everyone', payload));
    },
    async getFeaturesWaitlist({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get('/v1/su/features-waitlist'));
        commit('SET_SA_FEATURE_WAITLIST', responseData.waitlist);
    },
    setAllowFeature(_ctx, feature) {
        return axios.post('/v1/su/features/allow', feature)
            .then(() => {
                return true;
            }).catch(() => {
                return false;
            });
    },
    setRemoveFeature(_ctx, feature: string) {
        return axios.post('/v1/su/features/remove', feature)
            .then(() => {
                return true;
            }).catch(() => {
                return false;
            });
    },
    setAllowFeatureFromWishlist(_ctx, feature: string) {
        axios.post(`/v1/su/features-waitlist/allow/${feature}`)
            .then(() => {
                // commit('SET_SA_FEATURE_WAITLIST', response.data.waitlist);
            }).catch(() => {
        }).finally(() => {
        });
    },

    getFeatures({commit}) {
        axios.get('/v1/su/features')
            .then((response) => {
                commit('SET_SA_FEATURES_STATUS', response.data.featuresStatus);
            }).catch(() => {
        }).finally(() => {
        });
    },

    async getAllFeatures({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'features', () => axios.get('/v1/su/all-features'));

        commit('SET_SU_ALL_FEATURES', responseData.features);

        return responseData;
    },

    async testPdfCertSign({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'features', () => axios.post('/v1/su/pdf-cert-test'));

        commit('SET_SU_ALL_FEATURES', responseData.features);

        return responseData;
    },

    getCache({commit}) {
        axios.get('/v1/su/cache')
            .then((response) => {
                commit('SET_SA_CACHE', response.data.cache);
            }).catch(() => {
        }).finally(() => {
        });
    },

    clearCache(_ctx, data = null) {
        axios.delete('/v1/su/cache', {data})
            .catch(() => {
            }).finally(() => {
        });
    },

    async updateUser({commit}, user) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.put(`/v1/su/users/${user.uuid}`, user));
        commit('SET_CURRENT_WORKING_USER', responseData.user);

        push.success({
            message: 'User Saved',
        });
    },

    async fetchAllCompanies({commit}, data: {
        page?: number;
        per_page?: number;
        search?: string;
        embed?: string
    } = {}) {
        const page = data?.page ?? 1;
        const perPage = data?.per_page ?? 50;
        const search = data?.search?.trim() || undefined;
        const embed = data?.embed?.trim() || undefined;
        commit('SET_SU_LOADING', {subject: 'companies', loading: true});
        try {
            const response = await axios.get(`/v1/su/company`, {
                params: {page, per_page: perPage, ...(search && {search}), ...(embed && {embed})},
            });
            commit('SET_ALL_COMPANIES', response.data);
        } finally {
            commit('SET_SU_LOADING', {subject: 'companies', loading: false});
        }
    },

    async fetchAllMedia({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get('/v1/su/media'));
        commit(types.SET_ALL_MEDIA_DIRECTORIES, responseData.directories);
        return responseData;
    },
    async fetchDirectories({commit}, payload: { path: string }) {
        const urlParams = new URLSearchParams();
        urlParams.append('path', payload.path);
        const query = urlParams.toString();
        return await performApiRequest(commit, 'SET_SU_LOADING', 'directory', () => axios.get(`/v1/su/directory?${query}`));
    },

    async fetchServerDetails({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get('/v1/su/server-details'));
        commit(types.SET_ALL_SERVER_DETAILS, responseData.details);
    },

    async fetchAllMediaInDirectory({commit}, directory: string) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'users', () => axios.get(`/v1/su/media/directory?directory=${directory}`));
        commit(types.SET_ALL_MEDIA_IN_DIRECTORY, responseData.media);
        commit(types.SET_MEDIA_BASE_PATH, responseData.path);
        return responseData;
    },

    fetchAllListingImages({commit}) {
        axios.get('/v1/su/listing-images')
            .then((response) => {
                commit('SET_ALL_LISTING_IMAGES', response.data.images);
            }).catch(() => {
        }).finally(() => {
        });
    },

    resizeImage(_ctx, path) {
        axios.put('/v1/su/resize-image', {path})
            .then(() => {
            }).catch(() => {
        }).finally(() => {
        });
    },

    fetchOneCompany({commit}, uuid: string) {
        axios.get(`/v1/su/company/${uuid}`)
            .then((response) => {
                const company = {
                    ...response.data.company,
                    subscription: response.data.subscription ?? null,
                };
                commit('SET_CURRENT_WORKING_COMPANY', company);
            }).catch(() => {
        }).finally(() => {
        });
    },

    updateCompany({commit, state}) {
        axios.put(`/v1/su/company/${state.currentWorkingCompany.uuid}`, state.currentWorkingCompany)
            .then((response) => {
                commit('SET_CURRENT_WORKING_COMPANY', response.data.company);

                push.success({
                    message: 'Company Updated',
                });
            }).catch(() => {
        }).finally(() => {
        });
    },

    deleteCompany({commit, state}) {
        axios.delete(`/v1/su/company/${state.currentWorkingCompany.uuid}`)
            .then(() => {
                commit('SET_CURRENT_WORKING_COMPANY', {});
                push.success({
                    message: 'Company Deleted',
                });
                router.push({name: 'super.admin.companies'});
            }).catch(() => {
        }).finally(() => {
        });
    },

    getActiveUsersStats({commit}) {
        commit('SET_SU_LOADING', {subject: 'activeUsers', loading: true});

        axios.get('/v1/su/stats/users')
            .then((response) => {
                commit('SET_CURRENT_WORKING_SU_BASIC_STATS', {active_users: response.data.stats});
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'activeUsers', loading: false});
        });
    },

    getSubscribersStats({commit}) {
        commit('SET_SU_LOADING', {subject: 'subscribers', loading: true});

        axios.get('/v1/su/stats/subscribers')
            .then((response) => {
                commit('SET_CURRENT_WORKING_SU_BASIC_STATS', {subscribers: response.data.stats});
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'subscribers', loading: false});
        });
    },

    getListingStats({commit}) {
        commit('SET_SU_LOADING', {subject: 'listings', loading: true});

        axios.get('/v1/su/stats/listingStats')
            .then((response) => {
                commit('SET_CURRENT_WORKING_SU_BASIC_STATS', {listings: response.data.stats});
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'listings', loading: false});
        });
    },

    getConnectionsStats({commit}) {
        commit('SET_SU_LOADING', {subject: 'connections', loading: true});

        axios.get('/v1/su/stats/connectionsStats')
            .then((response) => {
                commit('SET_CURRENT_WORKING_SU_BASIC_STATS', {connections: response.data.stats});
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'connections', loading: false});
        });
    },

    getAutomations({commit}, data) {
        commit('SET_SU_LOADING', {subject: 'automations', loading: true});

        const query = createUrlQueryString(data);

        return axios.get(`/v1/su/stats/automations${query}`)
            .then((response) => {
                return response.data;
            }).catch(() => {
                return undefined;
            }).finally(() => {
                commit('SET_SU_LOADING', {subject: 'automations', loading: false});
            });
    },
    getAutomationsStats({commit}) {
        commit('SET_SU_LOADING', {subject: 'automations', loading: true});

        axios.get('/v1/su/stats/automationsStats')
            .then((response) => {
                commit('SET_CURRENT_WORKING_SU_BASIC_STATS', {automations: response.data.stats});
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'automations', loading: false});
        });
    },
    getAutomationsStatsData({commit}, data) {
        commit('SET_SU_LOADING', {subject: 'automations', loading: true});

        const query = createUrlQueryString(data);

        return axios.get(`/v1/su/stats/automationsStatsData${query}`)
            .then((response) => {
                return response.data;
            }).catch(() => {
                return undefined;
            }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'automations', loading: false});
        });
    },
    getTuroTrip({commit}, data) {
        commit('SET_SU_LOADING', {subject: 'turoTrip', loading: true});

        const query = createUrlQueryString(data);

        return axios.get(`/v1/su/stats/getTuroTrip${query}`)
            .then((response) => {
                return response.data;
            }).catch(() => {
                return undefined;
            }).finally(() => {
                commit('SET_SU_LOADING', {subject: 'turoTrip', loading: false});
            });
    },
    getBookingsStats({commit}) {
        commit('SET_SU_LOADING', {subject: 'bookings', loading: true});

        axios.get('/v1/su/stats/bookingStats')
            .then((response) => {
                commit('SET_CURRENT_WORKING_SU_BASIC_STATS', {bookings: response.data.stats});
            }).catch(() => {
        }).finally(() => {
            commit('SET_SU_LOADING', {subject: 'bookings', loading: false});
        });
    },

    async fetchAllRoles({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'roles', () => axios.get('/v1/roles'));
        commit('SET_ALL_ROLES', responseData.roles);
    },

    async fetchAllPermissions({commit}) {
        const responseData = await performApiRequest(commit, 'SET_SU_LOADING', 'permissions', () => axios.get('/v1/permissions'));
        commit('SET_ALL_PERMISSIONS', responseData.permissions);
    },

    createPermission({commit}, data) {
        axios.post('/v1/permissions', data)
            .then(async (response) => {
                commit('SET_ALL_PERMISSIONS', response.data.permissions);

                push.success({
                    message: 'Permission Created',
                });

                await router.push({name: 'super.admin.permissions'});
            }).catch(() => {
        }).finally(() => {
        });
    },

    updatePermission({commit}, data) {
        axios.put(`/v1/permissions/${data.id}`, data)
            .then((response) => {
                commit('SET_ALL_PERMISSIONS', response.data.permissions);
                push.success({
                    message: 'Permission Updated',
                });
            }).catch(() => {
        }).finally(() => {
        });
    },

    deletePermission({commit}, data) {
        axios.delete(`/v1/permissions/${data.id}`)
            .then(async (response) => {
                commit('SET_ALL_PERMISSIONS', response.data.permissions);
                push.success({
                    message: 'Permission Deleted',
                });
                await router.push({name: 'super.admin.permissions'});
            }).catch(() => {
        }).finally(() => {
        });
    },

    masqueradeInit({commit, rootGetters}, user) {
        axios.get(`/v1/users/${user.uuid}/masquerade`)
            .then((response) => {
                commit('app/SET_MASQUERADE', {
                    user: response.data.user,
                    token: response.data.token,
                    admin: {
                        email: rootGetters['auth/user'].email,
                        uuid: rootGetters['auth/user'].uuid,
                    },
                }, {root: true});
            }).catch(() => {
        }).finally(() => {
        });
    },

    async encryptText(_ctx, text: string): Promise<string> {
        try {
            const response = await axios.post('/v1/su/encrypt-text', {text});

            return response.data.result;
        } catch (e) {
            const message = axios.isAxiosError(e) ? (e.response?.data as { message?: string })?.message : undefined;
            throw new Error(message ?? 'There was an error');
        }
    },

    async decryptText(_ctx, text: string): Promise<string> {
        try {
            const response = await axios.post('/v1/su/decrypt-text', {text});

            return response.data.result;
        } catch (e) {
            const message = axios.isAxiosError(e) ? (e.response?.data as { message?: string })?.message : undefined;
            throw new Error(message ?? 'There was an error');
        }
    },

    async uploadMedia({commit}, formData: FormData) {
        return await performApiRequest(commit, 'SET_SU_LOADING', 'upload', () => axios.post('/v1/su/upload-media', formData));
    },
};

const superUserModule: Module<SuperUserState, RootState> = {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};

export default superUserModule;
